import React, { useState, useEffect, useMemo } from 'react';
import { Download, Search, GripVertical, RefreshCw, FileText, CheckSquare, Square, Edit2, Paperclip, CheckCircle, Clock } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api, { authUrl } from '../api';
import { formatEUR } from '../utils/format';
import { MonthContext } from '../context/MonthContext';

const DEFAULT_COLS = [
  { id: 'select', label: '☑', width: '40px' },
  { id: 'actions', label: 'Actions', width: '80px' },
  { id: 'date', label: 'Date', type: 'date', width: '100px' },
  { id: 'supplier_name', label: 'Fournisseur', width: '150px' },
  { id: 'total_amount', label: 'TTC', type: 'number', width: '80px' },
  { id: 'is_client_rebill', label: 'Traitée', type: 'boolean', width: '80px' },
  { id: 'project_name', label: 'Projet', width: '150px' },
  { id: 'title', label: 'Libellé', width: '150px' }
];

const SortableHeader = ({ col, sortBy, sortDir, filters, setFilters, handleSort, setPage, allSelected, handleSelectAll, width, onResize }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: width || col.width || '150px',
    minWidth: width || col.width || '150px',
    maxWidth: width || col.width || '150px',
    padding: 0,
    verticalAlign: 'top',
    background: 'var(--surface)',
    borderBottom: '2px solid var(--border)',
    zIndex: transform ? 1 : 0,
    position: 'relative'
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [col.id]: e.target.value }));
    setPage(1);
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.pageX;
    const startWidth = parseInt(width || col.width || '150', 10);
    
    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
      onResize(`${newWidth}px`);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid var(--glass-border)', minHeight: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexGrow: 1 }} onClick={() => handleSort(col.id)}>
          {col.id === 'select' ? (
            <input type="checkbox" checked={allSelected} onChange={handleSelectAll} onClick={(e) => e.stopPropagation()} />
          ) : (
            <>{col.label} {sortBy === col.id && (sortDir === 'asc' ? '↑' : '↓')}</>
          )}
        </div>
        {col.id !== 'select' && col.id !== 'actions' && (
          <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '2px', opacity: 0.5 }}>
            <GripVertical size={14} />
          </div>
        )}
      </div>
      <div style={{ padding: '0.5rem 1rem', minHeight: '52px', display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)' }}>
        {col.id !== 'select' && col.id !== 'actions' && col.type !== 'boolean' && col.type !== 'select' && (
          <input 
            type={col.type === 'date' ? 'date' : 'text'} 
            className="input-field" 
            style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '6px' }} 
            placeholder="Filtrer..." 
            value={filters[col.id] || ''} 
            onChange={handleFilterChange} 
          />
        )}
      </div>
      <div 
        onMouseDown={handleResizeMouseDown}
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 10, background: 'transparent' }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      />
    </th>
  );
};

const Rebilling = () => {
  const { selectedMonth } = React.useContext(MonthContext);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [filteredSum, setFilteredSum] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [page, setPage] = useState(1);
  const limit = 50; 
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  
  const [filters, setFilters] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  
  const [filterStatus, setFilterStatus] = useState('unprocessed');
  const [projectFilter, setProjectFilter] = useState('');
  const [availableProjectNames, setAvailableProjectNames] = useState([]);
  
  const [axonautProjects, setAxonautProjects] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [activeCols, setActiveCols] = useState(() => {
    const saved = localStorage.getItem('rebill_cols_v1');
    return saved ? JSON.parse(saved) : DEFAULT_COLS.map(c => c.id);
  });

  const [colWidths, setColWidths] = useState(() => {
    const saved = localStorage.getItem('rebill_col_widths_v1');
    return saved ? JSON.parse(saved) : {};
  });

  const handleResizeColumn = (colId, newWidth) => {
    setColWidths(prev => {
      const next = { ...prev, [colId]: newWidth };
      localStorage.setItem('rebill_col_widths_v1', JSON.stringify(next));
      return next;
    });
  };

  const columns = activeCols.map(id => DEFAULT_COLS.find(c => c.id === id)).filter(Boolean);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setActiveCols((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newArray = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('rebill_cols_v1', JSON.stringify(newArray));
        return newArray;
      });
    }
  };

  const loadProjects = async () => {
    try {
        const res = await api.get('/axonaut/projects');
        setAxonautProjects(res.data || []);
    } catch (err) {
        console.error("Erreur chargement projets Axonaut", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/rebilling/axonaut-expenses`, { params: { month_year: selectedMonth } });
      let expenses = res.data.data || [];

      // Projets disponibles (menu déroulant) — liste distincte sur le mois
      const projNames = [...new Set(expenses.map(e => e.project_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setAvailableProjectNames(projNames);
      if (projectFilter) {
        expenses = expenses.filter(e => e.project_name === projectFilter);
      }

      if (filterStatus === 'unprocessed') {
        expenses = expenses.filter(e => !e.is_client_rebill);
      } else if (filterStatus === 'processed') {
        expenses = expenses.filter(e => e.is_client_rebill);
      }
      if (search) {
        expenses = expenses.filter(e => Object.values(e).some(v => String(v).toLowerCase().includes(search.toLowerCase())));
      }
      Object.keys(filters).forEach(k => {
          if (filters[k]) {
             expenses = expenses.filter(e => String(e[k] || '').toLowerCase().includes(filters[k].toLowerCase()));
          }
      });

      // Total TTC calculé sur l'ensemble filtré (toutes pages), pas seulement la page affichée
      const sumTtc = expenses.reduce((s, e) => s + (parseFloat(e.total_amount) || 0), 0);
      setFilteredSum(sumTtc);

      expenses.sort((a, b) => {
         let valA = a[sortBy] || '';
         let valB = b[sortBy] || '';
         if (valA < valB) return sortDir === 'asc' ? -1 : 1;
         if (valA > valB) return sortDir === 'asc' ? 1 : -1;
         return 0;
      });
      
      const offset = (page - 1) * limit;
      const paginated = expenses.slice(offset, offset + limit);
      
      setData(paginated);
      setTotal(expenses.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadProjects();
  }, []);

  useEffect(() => { 
    const timer = setTimeout(() => {
      loadData(); 
    }, 300);
    return () => clearTimeout(timer);
  }, [page, search, sortBy, sortDir, filters, filterStatus, projectFilter, selectedMonth]);

  const handleSort = (field) => {
    if (field === 'select' || field === 'actions') return;
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(data.map(d => d.id));
    else setSelectedIds([]);
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) return;
    setProcessing(true);
    try {
            const selectedExpenses = data.filter(e => selectedIds.includes(e.id));
            // Nom de projet dérivé des dépenses sélectionnées (issues des filtres de colonnes)
            const projectNames = [...new Set(selectedExpenses.map(e => e.project_name).filter(Boolean))];
            const derivedProjectName = projectNames.length === 1 ? projectNames[0] : (projectNames.length > 1 ? 'Divers' : 'Sans Projet');
            const payload = {
                expenses: selectedExpenses,
                projectName: derivedProjectName,
                monthYear: selectedMonth
            };
        const res = await api.post('/rebilling/generate', payload);
        
        if (res.data.pdfUrl) {
            const pdfUrlFull = authUrl(api.defaults.baseURL.replace(/\/api$/, '') + res.data.pdfUrl);
            
            if ('showSaveFilePicker' in window) {
                try {
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: res.data.fileName,
                        types: [{ description: 'Document PDF', accept: { 'application/pdf': ['.pdf'] } }]
                    });
                    const response = await fetch(pdfUrlFull);
                    const blob = await response.blob();
                    const writable = await fileHandle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                } catch (err) {
                    if (err.name !== 'AbortError') console.error(err);
                }
            } else {
                const link = document.createElement('a');
                link.href = pdfUrlFull;
                link.setAttribute('download', res.data.fileName);
                link.setAttribute('target', '_blank');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            }
            setSelectedIds([]);
            loadData();
        }
    } catch (e) {
        alert("Erreur lors de la génération: " + (e.response?.data?.error || e.message));
    } finally {
        setProcessing(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({ ...row });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/expenses/${id}`, { ...editForm });
      setEditingId(null);
      loadData();
    } catch (e) {
      alert('Erreur modification');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/rebilling/axonaut-expenses/${id}/status`, { is_client_rebill: !currentStatus });
      loadData();
    } catch (e) {
      alert("Erreur lors du changement de statut");
    }
  };

  const handleMassStatusUpdate = async (newStatus) => {
    if (selectedIds.length === 0) return;
    try {
        await Promise.all(selectedIds.map(id => api.patch(`/rebilling/axonaut-expenses/${id}/status`, { is_client_rebill: newStatus })));
        setSelectedIds([]);
        loadData();
    } catch (e) {
        alert("Erreur lors de la mise à jour en masse");
    }
  };

  const renderCellContent = (col, row, isEditing) => {
    if (col.id === 'select') {
      return <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} />;
    }
    if (col.id === 'actions') {
      if (isEditing) {
        return (
          <>
            <span className="action-link" style={{ color: 'var(--success)', marginRight: '0.5rem' }} onClick={() => saveEdit(row.id)}>Ok</span>
            <span className="action-link" onClick={() => setEditingId(null)}>X</span>
          </>
        );
      }
      return (
        <div className="action-bar" style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          {row.pdf_path && (
            <a href={authUrl(`/api/pdf?path=${encodeURIComponent(row.pdf_path)}`)} target="_blank" rel="noreferrer" title="Ouvrir pièce jointe" className="action-link" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center' }}>
               <Paperclip size={16} />
            </a>
          )}
          <span className="action-link" style={{ display: 'flex', alignItems: 'center' }} onClick={() => startEdit(row)}>
             <Edit2 size={16} />
          </span>
        </div>
      );
    }

    if (isEditing && col.id !== 'id') {
      if (col.type === 'boolean') {
        return <input type="checkbox" checked={!!editForm[col.id]} onChange={e => setEditForm({...editForm, [col.id]: e.target.checked ? 1 : 0})} />;
      }
      if (col.id === 'project') {
          return (
            <input className="input-field" type="text" value={editForm[col.id] || ''} onChange={e => setEditForm({...editForm, [col.id]: e.target.value})} style={{ padding:'0.25rem', width: '100%' }} list="axonaut-projects-list" />
          );
      }
      return <input className="input-field" type={col.type === 'number' ? 'number' : 'text'} value={editForm[col.id] || ''} onChange={e => setEditForm({...editForm, [col.id]: e.target.value})} style={{ padding:'0.25rem', width: '100%' }} />;
    }

    let val = row[col.id];
    if (col.id === 'date') return val ? new Date(val).toLocaleDateString('fr-FR') : '-';
    if (col.type === 'number') return formatEUR(val) || '-';
    if (col.type === 'boolean') {
        if (val) return <span className="badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#059669', cursor: 'pointer' }} onClick={() => toggleStatus(row.id, val)}>Oui</span>;
        return <span className="badge" style={{ background: '#e0e0e0', color: '#1a1a1a', cursor: 'pointer' }} onClick={() => toggleStatus(row.id, val)}>Non</span>;
    }
    
    if (col.id === 'project' && val) {
        return <span style={{ fontWeight: 600, color: 'var(--brand)' }}>{val}</span>;
    }

    return val || '-';
  };

  const renderPagination = () => (
    <div className="pagination">
      <div>
        Affichage de {(page - 1) * limit + (total > 0 ? 1 : 0)} à {Math.min(page * limit, total)} sur {total}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn-icon" disabled={page === 1} onClick={() => setPage(page - 1)}>&lt;</button>
        <button className="btn-icon" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>&gt;</button>
      </div>
    </div>
  );


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
        <div className="header" style={{ marginBottom: '1rem' }}>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>Refacturation Client</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Générez des synthèses PDF avec les justificatifs pour refacturer vos clients.</p>
        </div>

        <datalist id="axonaut-projects-list">
            {axonautProjects.map(p => <option key={p.id} value={p.name} />)}
        </datalist>

        <div className="table-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', margin: 0, marginBottom: '0.5rem', background: 'var(--surface)', padding: '1rem', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
            {/* Recherche globale */}
            <div style={{ position: 'relative', width: '250px' }}>
                <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input 
                    type="text" placeholder="Recherche globale..." className="input-field" 
                    style={{ paddingLeft: '2.2rem', width: '100%', height: '36px', borderRadius: '8px' }}
                    value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
            </div>

            {/* Filtres Rapides */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                    <select className="input-field" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={{ padding: '0.5rem', width: 'auto' }}>
                      <option value="all">Toutes</option>
                      <option value="unprocessed">Non traitées</option>
                      <option value="processed">Traitées</option>
                    </select>
                </label>
                <select className="input-field" value={projectFilter} onChange={e => { setProjectFilter(e.target.value); setPage(1); }} style={{ padding: '0.5rem', width: 'auto', maxWidth: '260px' }} title="Filtrer par projet">
                  <option value="">Tous les projets</option>
                  {availableProjectNames.map(pn => <option key={pn} value={pn}>{pn}</option>)}
                </select>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                    className="btn" 
                    style={{ background: 'var(--success)', color: 'white', border: 'none', display: 'flex', gap: '6px', alignItems: 'center', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, width: 'auto', fontWeight: 600, padding: '0 12px', height: '36px', fontSize: '13px', borderRadius: '8px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer' }}
                    disabled={selectedIds.length === 0 || processing}
                    onClick={() => handleMassStatusUpdate(true)}
                >
                    <CheckCircle size={16} /> Marquer traitée
                </button>
                <button 
                    className="btn" 
                    style={{ background: 'var(--warning)', color: 'white', border: 'none', display: 'flex', gap: '6px', alignItems: 'center', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, width: 'auto', fontWeight: 600, padding: '0 12px', height: '36px', fontSize: '13px', borderRadius: '8px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer' }}
                    disabled={selectedIds.length === 0 || processing}
                    onClick={() => handleMassStatusUpdate(false)}
                >
                    <Clock size={16} /> Marquer non traitée
                </button>

                <button 
                    className="btn" 
                    style={{ background: '#3b82f6', color: '#ffffff', border: 'none', display: 'flex', gap: '6px', alignItems: 'center', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, width: 'auto', fontWeight: 600, padding: '0 12px', height: '36px', fontSize: '13px', borderRadius: '8px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                    disabled={selectedIds.length === 0 || processing}
                    onClick={handleGenerate}
                >
                    <FileText size={16} /> {processing ? 'Création...' : `Générer (${selectedIds.length})`}
                </button>
            </div>
        </div>

      <div className="glass-panel data-table-wrapper" style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column', flex: 1, padding: 0 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table style={{ minWidth: 'max-content', width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <SortableContext items={activeCols} strategy={horizontalListSortingStrategy}>
                <tr>
                  {columns.map(col => (
                    <SortableHeader 
                      key={col.id} col={col} width={colWidths[col.id] || col.width || '150px'}
                      onResize={(newWidth) => handleResizeColumn(col.id, newWidth)}
                      sortBy={sortBy} sortDir={sortDir} filters={filters} setFilters={setFilters} 
                      handleSort={handleSort} setPage={setPage}
                      allSelected={data.length > 0 && selectedIds.length === data.length} handleSelectAll={handleSelectAll}
                    />
                  ))}
                </tr>
              </SortableContext>
            </thead>
            <tbody>
              {!loading && data.length === 0 ? (
                <tr><td colSpan={columns.length} style={{ padding: '2rem', textAlign: 'center' }}>Aucune donnée correspondante.</td></tr>
              ) : !loading && data.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--glass-border)', background: row.is_client_rebill ? 'rgba(16, 185, 129, 0.03)' : 'transparent' }}>
                  {columns.map(col => (
                    <td key={col.id} title={row[col.id] ? String(row[col.id]) : ''} style={{ padding: '1rem', width: colWidths[col.id] || col.width || '150px', minWidth: colWidths[col.id] || col.width || '150px', maxWidth: colWidths[col.id] || col.width || '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {renderCellContent(col, row, editingId === row.id)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </DndContext>
      </div>

      {/* Total toujours visible, hors zone de défilement du tableau */}
      {!loading && total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1.25rem', background: 'var(--surface)', border: '1px solid var(--glass-border)', borderRadius: '12px', fontWeight: 700 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Total ({total} ligne{total > 1 ? 's' : ''}) :</span>
          <span style={{ color: 'var(--brand)', fontSize: '1.05rem' }}>{formatEUR(filteredSum)}</span>
        </div>
      )}

      <div style={{ marginTop: '-1rem' }}>
        {renderPagination()}
      </div>
    </div>
  );
};

export default Rebilling;
